import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Users, Award, Heart, Linkedin, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';

const About = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="about-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1709803857154-d20ee16ff763?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary/95"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {t.aboutUs}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {t.trustedPartner}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="company-story">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-serif text-3xl font-bold mb-6 text-foreground">
              {t.ourMission}
            </h2>
            <div className="bg-primary/5 border-l-4 border-primary p-6 mb-8">
              <p className="text-foreground font-medium leading-relaxed">
                {t.missionText}
              </p>
            </div>
            <p className="text-muted-foreground leading-relaxed italic">
              {t.governmentRelationsText}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl font-bold mb-6 text-foreground">
              {t.ourStory}
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                {language === 'fr'
                  ? "First Business Group S.A.R.L.U. est LA référence pour faciliter et soutenir les investisseurs en République Démocratique du Congo. Depuis plusieurs années, nous avons transformé le paysage de l'investissement en RDC en éliminant les obstacles et en créant des opportunités pour nos clients internationaux."
                  : "First Business Group S.A.R.L.U. is THE premier firm for facilitating and supporting investors in the Democratic Republic of Congo. For several years, we have transformed the DRC investment landscape by eliminating barriers and creating opportunities for our international clients."}
              </p>
              <p className="font-semibold text-foreground">
                {language === 'fr'
                  ? "Pourquoi First Business Group SARLU est votre partenaire idéal :"
                  : "Why First Business Group SARLU is your ideal partner:"}
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{language === 'fr' ? "Expertise locale inégalée avec des relations gouvernementales solides" : "Unmatched local expertise with strong government relations"}</li>
                <li>{language === 'fr' ? "Track record prouvé avec des clients majeurs (VELEX, ADVANCED GAMING, ARCADIA HOLDING, BUGAS HOLDING, CHEZZA GROUP)" : "Proven track record with major clients (VELEX, ADVANCED GAMING, ARCADIA HOLDING, BUGAS HOLDING, CHEZZA GROUP)"}</li>
                <li>{language === 'fr' ? "Support de bout en bout : de la consultation initiale au lancement opérationnel" : "End-to-end support: from initial consultation to operational launch"}</li>
                <li>{language === 'fr' ? "Navigation experte des complexités administratives et réglementaires" : "Expert navigation of administrative and regulatory complexities"}</li>
              </ul>
              <p>
                {language === 'fr'
                  ? "Dans le contexte d'un ralentissement économique mondial, la RDC représente une opportunité exceptionnelle. Notre mission est de transformer cette opportunité en succès concret pour vous. Un programme de relations gouvernementales solide reste un facteur crucial de succès en RDC, et c'est exactement ce que First Business Group SARLU vous offre."
                  : "In the context of a global economic slowdown, DRC represents an exceptional opportunity. Our mission is to transform this opportunity into concrete success for you. A strong government relations program remains a crucial factor for success in DRC, and this is exactly what First Business Group SARLU offers you."}
              </p>
              <p className="font-semibold text-primary text-lg">
                {language === 'fr'
                  ? "Contactez-nous dès aujourd'hui pour découvrir comment nous pouvons accélérer votre succès en RDC !"
                  : "Contact us today to discover how we can accelerate your success in DRC!"}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/5 via-background to-secondary/5" data-testid="ceo-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl font-bold text-foreground">
              {language === 'fr' ? 'Notre Fondateur' : 'Our Founder'}
            </h2>
            <div className="w-24 h-1 bg-secondary mx-auto mt-4"></div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-card border border-border shadow-2xl overflow-hidden"
          >
            <div className="flex flex-col lg:flex-row">
              {/* CEO Image */}
              <div className="lg:w-2/5 relative">
                <div className="aspect-[4/5] lg:aspect-auto lg:h-full relative overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_bizfirst/artifacts/662jcy6u_20200722_110228.jpg"
                    alt="Hugo Komba - CEO & Founder"
                    className="w-full h-full object-cover object-top"
                    data-testid="ceo-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent"></div>
                </div>
                {/* Decorative accent */}
                <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-primary"></div>
              </div>
              
              {/* CEO Info */}
              <div className="lg:w-3/5 p-8 lg:p-12 flex flex-col justify-center">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1 bg-secondary/10 text-secondary text-sm font-semibold tracking-wider uppercase mb-4">
                    CEO & {language === 'fr' ? 'Fondateur' : 'Founder'}
                  </span>
                  <h3 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-2">
                    Hugo Komba
                  </h3>
                  <p className="text-primary font-medium">
                    {language === 'fr' ? 'Conseiller d\'affaires et entrepreneur' : 'Business Advisor & Entrepreneur'}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    {language === 'fr' 
                      ? "Un conseiller d'affaires et entrepreneur de premier plan en RDC. Animé par une passion pour le développement de l'Afrique, en particulier la RDC."
                      : "A leading business advisor and entrepreneur in DRC. Driven by a passion for Africa's development, in particular DRC."}
                  </p>
                  <p className="text-foreground leading-relaxed text-lg font-medium italic border-l-4 border-secondary pl-4">
                    {language === 'fr'
                      ? "\"Je crois que la RDC possède un potentiel énorme.\""
                      : "\"I believe that DRC has enormous potential.\""}
                  </p>
                </div>
                
                {/* Decorative elements */}
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-border to-transparent"></div>
                  <div className="flex gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    <span className="w-2 h-2 bg-secondary rounded-full"></span>
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-l from-border to-transparent"></div>
                </div>

                {/* Social Media Links */}
                <div className="mt-6 flex items-center gap-4">
                  <span className="text-sm text-muted-foreground font-medium">
                    {language === 'fr' ? 'Suivez-moi :' : 'Follow me:'}
                  </span>
                  <div className="flex gap-3">
                    <a
                      href="https://www.linkedin.com/in/hugokomba/?originalSubdomain=cd"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center w-10 h-10 bg-primary/10 hover:bg-[#0A66C2] text-primary hover:text-white rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg"
                      data-testid="ceo-linkedin"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a
                      href="https://x.com/HugoKomba/with_replies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center justify-center w-10 h-10 bg-primary/10 hover:bg-black text-primary hover:text-white rounded-full transition-all duration-300 hover:scale-110 hover:shadow-lg"
                      data-testid="ceo-twitter"
                      aria-label="X (Twitter)"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted" data-testid="values-section">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl font-bold mb-12 text-center text-foreground"
          >
            {language === 'fr' ? 'Nos Valeurs' : 'Our Values'}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card p-8 border border-border hover:border-primary/50 transition-colors"
            >
              <Target className="h-12 w-12 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Excellence' : 'Excellence'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' 
                  ? 'Nous visons l\'excellence dans chaque aspect de notre service'
                  : 'We strive for excellence in every aspect of our service'}
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card p-8 border border-border hover:border-primary/50 transition-colors"
            >
              <Users className="h-12 w-12 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Partenariat' : 'Partnership'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' 
                  ? 'Nous construisons des relations durables avec nos clients'
                  : 'We build lasting relationships with our clients'}
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card p-8 border border-border hover:border-primary/50 transition-colors"
            >
              <Award className="h-12 w-12 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Expertise' : 'Expertise'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' 
                  ? 'Des années d\'expérience dans le marché congolais'
                  : 'Years of experience in the Congolese market'}
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-card p-8 border border-border hover:border-primary/50 transition-colors"
            >
              <Heart className="h-12 w-12 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Intégrité' : 'Integrity'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {language === 'fr' 
                  ? 'Transparence et honnêteté dans toutes nos interactions'
                  : 'Transparency and honesty in all our interactions'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="clients-section">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl font-bold mb-12 text-center text-foreground"
          >
            {language === 'fr' ? 'Nos Clients' : 'Our Clients'}
          </motion.h2>
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {['VELEX L.T.D.', 'ADVANCED GAMING S.A.R.L.', 'ARCADIA HOLDING S.A.R.L.', 'BUGAS HOLDING S.A.R.L.', 'CHEZZA GROUP S.A.R.L.', 'AVADA S.A.R.L.', 'SEPTIER COMMUNICATION L.T.D.', 'EAGLE PRESS L.T.D.'].map((client, idx) => (
              <div key={idx} className="bg-card border border-border p-6 flex items-center justify-center text-center hover:border-primary/50 transition-colors">
                <p className="font-mono text-xs text-muted-foreground">{client}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;